if (!customElements.get('printova-photo-uploader')) {
  class PrintovaPhotoUploader extends HTMLElement {
    connectedCallback() {
      // Guard against re-init when an ancestor (e.g. <printova-build-modal>)
      // portals to document.body — that move re-fires connectedCallback on
      // every descendant. Without this guard, state resets and event
      // listeners get attached a second time, causing every click handler
      // to fire twice (Preview, file input change, submit, etc.).
      if (this._initialized) return;
      this._initialized = true;
      this.cloudName = this.dataset.cloudName;
      this.uploadPreset = this.dataset.uploadPreset;
      this.requiredCount = parseInt(this.dataset.requiredCount, 10) || 30;
      this.maxFileSizeMb = parseInt(this.dataset.maxFileSizeMb, 10) || 10;
      this.productFormId = this.dataset.productFormId;
      this.maxConcurrent = 3;

      this.dropZone = this.querySelector('[data-drop-zone]');
      this.fileInput = this.querySelector('[data-file-input]');
      this.swapFileInput = this.querySelector('[data-swap-input]');
      this.thumbsContainer = this.querySelector('[data-thumbs]');
      this.counter = this.querySelector('[data-counter]');
      this.helperText = this.querySelector('[data-helper]');
      this.messageEl = this.querySelector('[data-message]');
      this.previewBtn = this.querySelector('[data-preview-btn]');
      this.arrangeHint = this.querySelector('[data-arrange-hint]');
      this.urlsInput = this.querySelector('[data-urls-input]');
      this.summaryInput = this.querySelector('[data-summary-input]');
      this.tripTitleEl = this.querySelector('[data-trip-title]');
      this.tripDescEl = this.querySelector('[data-trip-desc]');
      this.tripTitleCounter = this.querySelector('[data-trip-title-counter]');
      this.tripDescCounter = this.querySelector('[data-trip-desc-counter]');
      this.tripTitleInput = this.querySelector('[data-trip-title-input]');
      this.tripDescInput = this.querySelector('[data-trip-desc-input]');
      this.instructionsEl = document.querySelector('[data-instructions]');
      this.finalSubmitBtn = document.querySelector('[data-final-submit]');
      this.finalStatusEl = document.querySelector('[data-final-status]');
      if (this.finalSubmitBtn) {
        this._finalSubmitText = this.finalSubmitBtn.textContent;
        this.finalSubmitBtn.addEventListener('click', () => this.handleFinalSubmit());
      }

      this.photos = [];
      this.uploadQueue = [];
      this.activeUploads = 0;
      this.mode = 'upload';
      this.sortable = null;
      this.swapTargetId = null;
      this.tripTitle = '';
      this.tripDesc = '';

      this.cropModal = document.querySelector('printova-crop-modal');
      this.bookPreview = document.querySelector('printova-book-preview');

      this.bindEvents();
      this.bindTripInputs();
      this.syncForm();
      this.updateCounter();
      this.updateTripCounters();
      this.updateSubmitGate();
    }

    bindTripInputs() {
      if (this.tripTitleEl) {
        this.tripTitleEl.addEventListener('input', () => {
          this.tripTitle = this.tripTitleEl.value;
          this.updateTripCounters();
          this.syncForm();
          this.updateSubmitGate();
        });
      }
      if (this.tripDescEl) {
        this.tripDescEl.addEventListener('input', () => {
          this.tripDesc = this.tripDescEl.value;
          this.updateTripCounters();
          this.syncForm();
        });
      }
    }

    updateTripCounters() {
      if (this.tripTitleCounter) {
        const len = this.tripTitle.length;
        const max = this.tripTitleEl ? parseInt(this.tripTitleEl.getAttribute('maxlength'), 10) || 40 : 40;
        this.tripTitleCounter.textContent = `${len} / ${max}`;
        this.tripTitleCounter.classList.toggle('is-near-limit', len >= max * 0.9 && len < max);
        this.tripTitleCounter.classList.toggle('is-at-limit', len >= max);
      }
      if (this.tripDescCounter) {
        const len = this.tripDesc.length;
        const max = this.tripDescEl ? parseInt(this.tripDescEl.getAttribute('maxlength'), 10) || 200 : 200;
        this.tripDescCounter.textContent = `${len} / ${max}`;
        this.tripDescCounter.classList.toggle('is-near-limit', len >= max * 0.9 && len < max);
        this.tripDescCounter.classList.toggle('is-at-limit', len >= max);
      }
    }

    bindEvents() {
      this.dropZone.addEventListener('click', () => this.fileInput.click());
      this.dropZone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.fileInput.click();
        }
      });

      ['dragenter', 'dragover'].forEach((ev) => {
        this.dropZone.addEventListener(ev, (e) => {
          e.preventDefault();
          this.dropZone.classList.add('is-dragover');
        });
      });
      ['dragleave', 'dragend'].forEach((ev) => {
        this.dropZone.addEventListener(ev, () => {
          this.dropZone.classList.remove('is-dragover');
        });
      });
      this.dropZone.addEventListener('drop', (e) => {
        e.preventDefault();
        this.dropZone.classList.remove('is-dragover');
        if (e.dataTransfer && e.dataTransfer.files) {
          this.handleFiles(e.dataTransfer.files);
        }
      });

      this.fileInput.addEventListener('change', (e) => {
        this.handleFiles(e.target.files);
        this.fileInput.value = '';
      });

      this.swapFileInput.addEventListener('change', (e) => {
        const id = this.swapTargetId;
        this.swapTargetId = null;
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!id || !file) return;
        this.handleSwap(id, file);
      });

      this.previewBtn.addEventListener('click', () => this.openBookPreview());
    }

    /* ---------- File validation + initial upload ---------- */

    handleFiles(fileList) {
      const files = Array.from(fileList);
      const remaining = this.requiredCount - this.photos.length;

      if (remaining <= 0) {
        this.showMessage(
          `Maximum of ${this.requiredCount} photos. Remove one to add another.`,
          'error'
        );
        return;
      }

      if (files.length > remaining) {
        this.showMessage(
          `You can add at most ${remaining} more photo${remaining === 1 ? '' : 's'}. ${files.length - remaining} skipped.`,
          'error'
        );
      }

      const slice = files.slice(0, remaining);
      for (const file of slice) {
        if (!this.validateFile(file)) continue;
        this.enqueueUpload(file);
      }
    }

    validateFile(file) {
      // Pre-compression: only check that it's an image. Size is checked
      // AFTER client-side compression in uploadPhoto/handleSwap.
      if (!file.type.startsWith('image/') && !this.isHeic(file)) {
        this.showMessage(`"${file.name}" is not an image — skipped.`, 'error');
        return false;
      }
      return true;
    }

    isHeic(file) {
      const t = (file.type || '').toLowerCase();
      const n = (file.name || '').toLowerCase();
      return t === 'image/heic' || t === 'image/heif' || n.endsWith('.heic') || n.endsWith('.heif');
    }

    /* ---------- Client-side compression ---------- */

    async compressFile(file) {
      try {
        const bitmap = await this.decodeFile(file);
        const w = bitmap.width || bitmap.naturalWidth || 0;
        const h = bitmap.height || bitmap.naturalHeight || 0;
        if (!w || !h) throw new Error('Decoded image has no dimensions');

        const maxEdge = 2400;
        const scale = Math.min(1, maxEdge / Math.max(w, h));
        const dw = Math.max(1, Math.round(w * scale));
        const dh = Math.max(1, Math.round(h * scale));

        const canvas = document.createElement('canvas');
        canvas.width = dw;
        canvas.height = dh;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2d context unavailable');
        ctx.drawImage(bitmap, 0, 0, dw, dh);
        if (typeof bitmap.close === 'function') bitmap.close();

        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
            'image/jpeg',
            0.85
          );
        });
        return blob;
      } catch (err) {
        console.warn('[printova] compressFile failed, using original:', err);
        return file;
      }
    }

    async decodeFile(file) {
      // createImageBitmap is fast and natively respects EXIF orientation.
      if ('createImageBitmap' in window) {
        try {
          return await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch (e) {
          // Fall through to <img> fallback (some browsers can't decode HEIC).
        }
      }
      return await new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(img);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Image decode failed'));
        };
        img.src = url;
      });
    }

    heicHint(originalFile) {
      if (!this.isHeic(originalFile)) return null;
      return 'Your iPhone HEIC photo couldn\'t be optimized here. In iPhone Settings → Camera → Formats, choose "Most Compatible" to save as JPG, then re-export this photo.';
    }

    enqueueUpload(file) {
      const id = `p-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const localUrl = URL.createObjectURL(file);
      const photo = {
        id,
        file,
        url: null,
        publicId: null,
        width: 0,
        height: 0,
        localUrl,
        crop: { x: 0, y: 0, width: 1, height: 1, auto: true },
        status: 'queued',
      };
      this.photos.push(photo);
      this.renderThumb(photo);
      this.updateCounter();
      this.updateSubmitGate();
      this.uploadQueue.push(photo);
      this.processQueue();
    }

    processQueue() {
      while (this.activeUploads < this.maxConcurrent && this.uploadQueue.length > 0) {
        const photo = this.uploadQueue.shift();
        if (this.photos.indexOf(photo) === -1) continue;
        this.activeUploads++;
        this.uploadPhoto(photo).finally(() => {
          this.activeUploads--;
          this.processQueue();
        });
      }
    }

    async uploadPhoto(photo) {
      this.setPhotoStatus(photo, 'uploading');
      try {
        const working = await this.compressFile(photo.file);
        const maxBytes = this.maxFileSizeMb * 1024 * 1024;
        if (working.size > maxBytes) {
          const hint = this.heicHint(photo.file);
          throw new Error(
            hint ||
              `Photo is ${(working.size / 1024 / 1024).toFixed(1)}MB after optimization — too large to upload.`
          );
        }
        const json = await this.cloudinaryUpload(working);
        photo.url = json.secure_url;
        photo.publicId = json.public_id;
        photo.width = json.width || 0;
        photo.height = json.height || 0;
        this.setPhotoStatus(photo, 'uploaded');
        this.syncForm();
        this.updateSubmitGate();
        this.checkModeTransition();
      } catch (err) {
        console.error('[printova] upload failed:', err);
        photo.error = err.message || 'Upload failed';
        this.setPhotoStatus(photo, 'error');
        this.updateSubmitGate();
        this.showMessage(err.message || 'Upload failed', 'error');
      }
    }

    async cloudinaryUpload(file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', this.uploadPreset);
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${this.cloudName}/image/upload`,
        { method: 'POST', body: formData }
      );
      if (!res.ok) {
        let detail = '';
        try {
          const errJson = await res.json();
          detail = errJson && errJson.error && errJson.error.message ? errJson.error.message : '';
        } catch (_) {}
        throw new Error(detail || `Upload failed (HTTP ${res.status})`);
      }
      const json = await res.json();
      if (!json.secure_url) throw new Error('Upload response missing URL.');
      return json;
    }

    /* ---------- Tile rendering ---------- */

    setPhotoStatus(photo, status) {
      photo.status = status;
      const tile = this.thumbsContainer.querySelector(`[data-photo-id="${photo.id}"]`);
      if (tile) tile.dataset.status = status;
    }

    renderThumb(photo) {
      const tile = this.buildTile(photo);
      this.thumbsContainer.appendChild(tile);
    }

    buildTile(photo) {
      const tile = document.createElement('div');
      tile.className = 'printova-thumb';
      tile.dataset.photoId = photo.id;
      tile.dataset.status = photo.status;

      const imgSrc = this.tileImageSrc(photo);

      if (this.mode === 'upload') {
        tile.innerHTML = `
          <img class="printova-thumb__img" src="${imgSrc}" alt="">
          <div class="printova-thumb__overlay" aria-hidden="true">
            <span class="printova-thumb__spinner"></span>
          </div>
          <button type="button" class="printova-thumb__icon-btn printova-thumb__retry" aria-label="Retry upload">Retry</button>
          <button type="button" class="printova-thumb__icon-btn printova-thumb__remove" aria-label="Remove photo">
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path fill="currentColor" d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z"/>
            </svg>
          </button>
        `;
        tile.querySelector('.printova-thumb__remove').addEventListener('click', (e) => {
          e.stopPropagation();
          this.removePhoto(photo.id);
        });
        tile.querySelector('.printova-thumb__retry').addEventListener('click', (e) => {
          e.stopPropagation();
          this.retryUpload(photo);
        });
      } else {
        tile.innerHTML = `
          <img class="printova-thumb__img" src="${imgSrc}" alt="">
          <div class="printova-thumb__overlay" aria-hidden="true">
            <span class="printova-thumb__spinner"></span>
          </div>
          <div class="printova-thumb__order" aria-hidden="true"></div>
          <span class="printova-thumb__opening-badge" aria-hidden="true">Opening page</span>
          <div class="printova-thumb__actions">
            <button type="button" class="printova-thumb__icon-btn printova-thumb__crop" aria-label="Crop photo" title="Crop">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M7 1v4H3v2h4v10a2 2 0 0 0 2 2h10v4h2v-4h4v-2H9V1H7zm2 6h8v8H9V7z"/>
              </svg>
            </button>
            <button type="button" class="printova-thumb__icon-btn printova-thumb__swap" aria-label="Replace photo" title="Replace">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M7 7h8v3l5-4-5-4v3H5v6h2V7zm10 10H9v-3l-5 4 5 4v-3h10v-6h-2v4z"/>
              </svg>
            </button>
          </div>
        `;
        tile.querySelector('.printova-thumb__crop').addEventListener('click', (e) => {
          e.stopPropagation();
          this.openCropModal(photo.id);
        });
        tile.querySelector('.printova-thumb__swap').addEventListener('click', (e) => {
          e.stopPropagation();
          this.beginSwap(photo.id);
        });
      }
      return tile;
    }

    refreshTile(id) {
      const oldTile = this.thumbsContainer.querySelector(`[data-photo-id="${id}"]`);
      if (!oldTile) return;
      const photo = this.photos.find((p) => p.id === id);
      if (!photo) return;
      const newTile = this.buildTile(photo);
      oldTile.replaceWith(newTile);
    }

    tileImageSrc(photo) {
      if (photo.status === 'uploaded' && photo.publicId) {
        return this.cropUrl(photo, 240);
      }
      return photo.localUrl || '';
    }

    cropUrl(photo, width) {
      const base = `https://res.cloudinary.com/${this.cloudName}/image/upload`;
      const delivery = `q_auto,f_auto,w_${width}`;
      if (!photo.crop || photo.crop.auto) {
        return `${base}/c_fill,g_auto,ar_1000:1414/${delivery}/${photo.publicId}`;
      }
      const fmt = (n) => Number(n).toFixed(4);
      const { x, y, width: w, height: h } = photo.crop;
      return `${base}/c_crop,x_${fmt(x)},y_${fmt(y)},w_${fmt(w)},h_${fmt(h)}/c_fill,ar_1000:1414/${delivery}/${photo.publicId}`;
    }

    /* ---------- Phase 1 actions (remove, retry) ---------- */

    removePhoto(id) {
      const idx = this.photos.findIndex((p) => p.id === id);
      if (idx === -1) return;
      const photo = this.photos[idx];
      if (photo.localUrl) URL.revokeObjectURL(photo.localUrl);
      this.photos.splice(idx, 1);
      const tile = this.thumbsContainer.querySelector(`[data-photo-id="${id}"]`);
      if (tile) tile.remove();
      this.updateCounter();
      this.syncForm();
      this.updateSubmitGate();
      this.checkModeTransition();
    }

    retryUpload(photo) {
      if (photo.status !== 'error') return;
      photo.error = null;
      this.uploadQueue.push(photo);
      this.processQueue();
    }

    /* ---------- Mode transitions ---------- */

    checkModeTransition() {
      const uploaded = this.photos.filter((p) => p.status === 'uploaded').length;
      const inFlight = this.photos.filter(
        (p) => p.status === 'queued' || p.status === 'uploading'
      ).length;
      const errored = this.photos.filter((p) => p.status === 'error').length;

      if (
        this.mode === 'upload' &&
        uploaded === this.requiredCount &&
        inFlight === 0 &&
        errored === 0
      ) {
        this.setMode('arrange');
      } else if (
        this.mode === 'arrange' &&
        (uploaded < this.requiredCount || inFlight > 0 || errored > 0)
      ) {
        this.setMode('upload');
      }
    }

    setMode(newMode) {
      if (this.mode === newMode) return;
      if (this.sortable) {
        try { this.sortable.destroy(); } catch (_) {}
        this.sortable = null;
      }
      this.mode = newMode;
      this.dataset.mode = newMode;

      this.thumbsContainer.innerHTML = '';
      for (const photo of this.photos) {
        this.thumbsContainer.appendChild(this.buildTile(photo));
      }

      this.previewBtn.hidden = newMode !== 'arrange';
      this.arrangeHint.hidden = newMode !== 'arrange';
      if (this.instructionsEl) this.instructionsEl.hidden = newMode === 'arrange';

      if (newMode === 'arrange' && window.Sortable) {
        this.sortable = window.Sortable.create(this.thumbsContainer, {
          animation: 150,
          delay: 80,
          delayOnTouchOnly: true,
          filter: '.printova-thumb__icon-btn',
          preventOnFilter: false,
          ghostClass: 'printova-thumb--ghost',
          chosenClass: 'printova-thumb--chosen',
          dragClass: 'printova-thumb--drag',
          onEnd: (e) => {
            if (e.oldIndex === e.newIndex) return;
            const [moved] = this.photos.splice(e.oldIndex, 1);
            this.photos.splice(e.newIndex, 0, moved);
            this.updateOrderBadges();
            this.syncForm();
            this.updateSubmitGate();
          },
        });
        this.updateOrderBadges();
      }
    }

    updateOrderBadges() {
      const tiles = this.thumbsContainer.querySelectorAll('.printova-thumb');
      tiles.forEach((tile, idx) => {
        const badge = tile.querySelector('.printova-thumb__order');
        if (badge) badge.textContent = String(idx + 1);
        if (idx === 0) tile.dataset.isFirst = 'true';
        else delete tile.dataset.isFirst;
      });
    }

    /* ---------- Arrange-mode actions ---------- */

    openCropModal(id) {
      const photo = this.photos.find((p) => p.id === id);
      if (!photo || !this.cropModal) return;
      this.cropModal.open({
        photo,
        cloudName: this.cloudName,
        onConfirm: (newCrop) => {
          photo.crop = newCrop;
          this.refreshTile(id);
          this.syncForm();
          this.updateSubmitGate();
        },
      });
    }

    openBookPreview() {
      if (!this.bookPreview) return;
      this.bookPreview.open({
        photos: this.photos,
        cloudName: this.cloudName,
        tripTitle: this.tripTitle,
        tripDesc: this.tripDesc,
      });
    }

    /* ---------- Per-tile swap ---------- */

    beginSwap(id) {
      this.swapTargetId = id;
      this.swapFileInput.click();
    }

    async handleSwap(id, file) {
      if (!this.validateFile(file)) return;
      const idx = this.photos.findIndex((p) => p.id === id);
      if (idx === -1) return;
      const photo = this.photos[idx];

      const backup = {
        url: photo.url,
        publicId: photo.publicId,
        width: photo.width,
        height: photo.height,
        crop: photo.crop,
        status: photo.status,
      };

      photo.status = 'uploading';
      this.refreshTile(id);
      this.updateSubmitGate();

      try {
        const working = await this.compressFile(file);
        const maxBytes = this.maxFileSizeMb * 1024 * 1024;
        if (working.size > maxBytes) {
          const hint = this.heicHint(file);
          throw new Error(
            hint ||
              `Photo is ${(working.size / 1024 / 1024).toFixed(1)}MB after optimization — too large to upload.`
          );
        }
        const json = await this.cloudinaryUpload(working);
        photo.url = json.secure_url;
        photo.publicId = json.public_id;
        photo.width = json.width || 0;
        photo.height = json.height || 0;
        photo.crop = { x: 0, y: 0, width: 1, height: 1, auto: true };
        photo.status = 'uploaded';
        this.refreshTile(id);
        this.syncForm();
        this.updateSubmitGate();
      } catch (err) {
        console.error('[printova] swap upload failed:', err);
        Object.assign(photo, backup);
        this.refreshTile(id);
        this.showMessage(err.message || `Couldn't replace that photo. Please try again.`, 'error');
        this.updateSubmitGate();
      }
    }

    /* ---------- UI feedback ---------- */

    updateCounter() {
      this.counter.textContent = `${this.photos.length} / ${this.requiredCount} photos uploaded`;
    }

    syncForm() {
      const uploaded = this.photos.filter((p) => p.status === 'uploaded');

      const payload = uploaded.map((p, idx) => ({
        url: p.url,
        publicId: p.publicId,
        crop: {
          x: p.crop.x,
          y: p.crop.y,
          width: p.crop.width,
          height: p.crop.height,
          auto: !!p.crop.auto,
        },
        order: idx,
      }));

      this.urlsInput.value = JSON.stringify(payload);

      if (this.mode === 'arrange' && uploaded.length === this.requiredCount) {
        this.summaryInput.value = `${this.requiredCount} photos arranged, ready for print`;
      } else {
        this.summaryInput.value = `${uploaded.length} photo${uploaded.length === 1 ? '' : 's'} attached`;
      }

      if (this.tripTitleInput) {
        this.tripTitleInput.value = this.tripTitle;
      }
      if (this.tripDescInput) {
        const trimmed = this.tripDesc.trim();
        if (trimmed) {
          this.tripDescInput.setAttribute('name', 'properties[Trip Description]');
          this.tripDescInput.value = this.tripDesc;
        } else {
          this.tripDescInput.removeAttribute('name');
          this.tripDescInput.value = '';
        }
      }
    }

    updateSubmitGate() {
      const uploaded = this.photos.filter((p) => p.status === 'uploaded').length;
      const inFlight = this.photos.filter(
        (p) => p.status === 'queued' || p.status === 'uploading'
      ).length;
      const errored = this.photos.filter((p) => p.status === 'error').length;
      const allHaveCrop = this.photos.every((p) => p.crop);
      const hasTripTitle = this.tripTitle.trim().length > 0;

      const productForm = document.getElementById(this.productFormId);
      const submitBtn = productForm
        ? productForm.querySelector('button[type="submit"][name="add"]')
        : null;

      let canSubmit = false;
      let helperMessage = '';

      if (inFlight > 0) {
        helperMessage = `Uploading… ${uploaded} of ${this.requiredCount} ready.`;
      } else if (errored > 0) {
        helperMessage = `${errored} upload${errored === 1 ? '' : 's'} failed. Retry or remove to continue.`;
      } else if (uploaded < this.requiredCount) {
        const need = this.requiredCount - uploaded;
        helperMessage = `Upload ${need} more photo${need === 1 ? '' : 's'} to continue.`;
      } else if (!hasTripTitle) {
        helperMessage = 'Add a trip title to continue.';
      } else if (!allHaveCrop) {
        helperMessage = 'Some photos still need a crop.';
      } else {
        canSubmit = true;
      }

      if (submitBtn) {
        if (canSubmit) {
          submitBtn.removeAttribute('disabled');
          submitBtn.removeAttribute('aria-disabled');
        } else {
          submitBtn.setAttribute('disabled', 'disabled');
          submitBtn.setAttribute('aria-disabled', 'true');
        }
      }

      if (this.finalSubmitBtn) {
        if (canSubmit) {
          this.finalSubmitBtn.removeAttribute('disabled');
          this.finalSubmitBtn.removeAttribute('aria-disabled');
        } else {
          this.finalSubmitBtn.setAttribute('disabled', 'disabled');
          this.finalSubmitBtn.setAttribute('aria-disabled', 'true');
        }
      }

      if (this.finalStatusEl) {
        this.finalStatusEl.textContent = canSubmit
          ? `${this.requiredCount} photos ready • opening page set`
          : helperMessage;
      }

      if (helperMessage) {
        this.helperText.textContent = helperMessage;
        this.helperText.hidden = false;
      } else {
        this.helperText.textContent = '';
        this.helperText.hidden = true;
      }
    }

    async handleFinalSubmit() {
      const uploaded = this.photos.filter((p) => p.status === 'uploaded').length;
      const inFlight = this.photos.filter(
        (p) => p.status === 'queued' || p.status === 'uploading'
      ).length;
      const errored = this.photos.filter((p) => p.status === 'error').length;
      const hasTripTitle = this.tripTitle.trim().length > 0;

      if (
        uploaded !== this.requiredCount ||
        inFlight > 0 ||
        errored > 0 ||
        !hasTripTitle
      ) {
        return;
      }

      const productForm = document.getElementById(this.productFormId);
      if (!productForm) {
        console.error('[printova] product form not found:', this.productFormId);
        this.showMessage('Could not find product form. Refresh and try again.', 'error');
        return;
      }

      const btn = this.finalSubmitBtn;
      btn.classList.add('is-loading');
      btn.setAttribute('disabled', 'disabled');
      btn.textContent = 'Adding to cart…';

      try {
        const formData = new FormData(productForm);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
          },
          body: formData,
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.status) {
          throw new Error(json.description || json.message || `Cart error (${res.status})`);
        }
        window.location.href = '/cart';
      } catch (err) {
        console.error('[printova] cart add failed:', err);
        this.showMessage(
          err.message || 'Could not add to cart. Please try again.',
          'error'
        );
        btn.classList.remove('is-loading');
        btn.textContent = this._finalSubmitText || 'Add to Cart';
        this.updateSubmitGate();
      }
    }

    showMessage(msg, level) {
      if (!this.messageEl) return;
      this.messageEl.textContent = msg;
      this.messageEl.dataset.level = level || 'info';
      this.messageEl.hidden = false;
      clearTimeout(this._msgTimer);
      this._msgTimer = setTimeout(() => {
        this.messageEl.hidden = true;
      }, 5000);
    }
  }

  customElements.define('printova-photo-uploader', PrintovaPhotoUploader);
}
